import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Upload } from 'lucide-react';

import useTitleApi, { MISSING, TITLE_COLUMN_NAMES } from './config';
import TitleBatchAssignDrawer from './components/TitleBatchAssignDrawer';
import Button from '../../components/ui/Button';
import ArchitectureCoverageNotice from '../../components/organization/ArchitectureCoverageNotice';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// URL ön-süzgeci (kapsama uyarısındaki "Göster"): ?gradeMissing=1 / ?jobFamilyMissing=1.
function initialFilters(search) {
  const qs = new URLSearchParams(search);
  const params = { pageSize: 10, pageNumber: 1 };
  const filterList = TITLE_COLUMN_NAMES.map(() => []);
  if (qs.get('gradeMissing') === '1') {
    params['gradeId[eq]'] = MISSING;
    filterList[TITLE_COLUMN_NAMES.indexOf('gradeId')] = [MISSING];
  }
  if (qs.get('jobFamilyMissing') === '1') {
    params['jobFamilyId[eq]'] = MISSING;
    filterList[TITLE_COLUMN_NAMES.indexOf('jobFamilyId')] = [MISSING];
  }
  return { params, filterList };
}

export default function TitlesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [initial] = useState(() => initialFilters(location.search));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState(initial.params);
  const [filterList, setFilterList] = useState(initial.filterList);
  const [batch, setBatch] = useState({ open: false, mode: 'grade', ids: [] });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, batchAssign,
    gradeAsync, familyAsync, refreshTrigger,
  } = useTitleApi(queryParams);

  // Toplu kademe / iş ailesi ataması — seçim çubuğu eylemleri (DataTable selectionActions).
  const selectionActions = useMemo(() => (canWrite('EM') ? [
    { key: 'assign-grade', label: t('title.assignGrade'), onClick: (rows) => setBatch({ open: true, mode: 'grade', ids: rows.map((r) => r.id) }) },
    { key: 'assign-family', label: t('title.assignJobFamily'), onClick: (rows) => setBatch({ open: true, mode: 'family', ids: rows.map((r) => r.id) }) },
  ] : undefined), [t, canWrite]);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('title.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('title.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('title.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('title.edit') : t('title.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <TitleBatchAssignDrawer
        open={batch.open}
        mode={batch.mode}
        ids={batch.ids}
        gradeAsync={gradeAsync}
        familyAsync={familyAsync}
        onClose={() => setBatch((b) => ({ ...b, open: false }))}
        onAssign={batchAssign}
      />

      {canWrite('EM') && <ArchitectureCoverageNotice refreshKey={refreshTrigger} />}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('title.deleteTitle')}
        message={t('title.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('title.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('EM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
          selectionActions={selectionActions}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          toolbarActions={canWrite('EM') ? (
            <Button variant="outline" onClick={() => history.push('/job-architecture/import')} data-action="import-architecture">
              <Upload size={14} />
              {' '}
              {t('jobArchitectureImport.open')}
            </Button>
          ) : null}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite, history,
        handleNewButtonClick, handleRowsDelete, filterList, selectionActions,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
